import { Component, OnInit, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';
import { SharedService } from 'app/shared/services/shared.service';

@Component({
  selector: 'colorpicker-component',
  templateUrl: './colorpicker.component.html',
  styleUrls: ['./colorpicker.component.scss'],
  animations: [
    trigger('showAndHideAnimation', [
      state(
        'hide',
        style({
          visibility: 'hidden',
          opacity: 0,
        })
      ),
      state(
        'show',
        style({
          visibility: 'visible',
          opacity: 1,
        })
      ),
      transition('hide <=> show', animate('200ms')),
    ]),
  ],
})
export class ColorpickerComponent implements OnInit {
  public active: boolean;
  @Input() public color: string;
  public hue: string;
  public colorInput: string;
  public colorStrip: HTMLCanvasElement;
  public colorStripCtx: CanvasRenderingContext2D;
  public saturationMap: HTMLCanvasElement;
  public saturationMapCtx: CanvasRenderingContext2D;
  public hueIsDragged: boolean;
  public saturationIsDragged: boolean;
  @Output() public onChange: EventEmitter<string>;

  constructor(public sanitizer: DomSanitizer, public sharedService: SharedService) {
    this.active = false;
    this.color = 'ff0000';
    this.hue = 'ff0000';
    this.onChange = new EventEmitter<string>();
    this.hueIsDragged = false;
    this.saturationIsDragged = false;
  }

  ngOnInit() {
    this.colorStrip = document.getElementsByClassName('colorpickerHue')[0].getElementsByTagName('canvas')[0];
    this.colorStripCtx = this.colorStrip.getContext('2d');
    this.saturationMap = document.getElementsByClassName('colorpickerSaturation')[0].getElementsByTagName('canvas')[0];
    this.saturationMapCtx = this.saturationMap.getContext('2d');
    this.initColorsHueMap();
    this.updateColorsSaturationMap();
  }

  ngOnChanges($event) {
    this.hue = $event.color.currentValue;
  }

  initColorsHueMap() {
    const rainbow: CanvasGradient = this.colorStripCtx.createLinearGradient(0, 0, this.colorStrip.width, 0);
    this.colorStripCtx.rect(0, 0, this.colorStrip.width, this.colorStrip.height);
    rainbow.addColorStop(0, 'rgba(255, 0, 0, 1)');
    rainbow.addColorStop(0.17, 'rgba(255, 255, 0, 1)');
    rainbow.addColorStop(0.34, 'rgba(0, 255, 0, 1)');
    rainbow.addColorStop(0.51, 'rgba(0, 255, 255, 1)');
    rainbow.addColorStop(0.68, 'rgba(0, 0, 255, 1)');
    rainbow.addColorStop(0.85, 'rgba(255, 0, 255, 1)');
    rainbow.addColorStop(1, 'rgba(255, 0, 0, 1)');
    this.colorStripCtx.fillStyle = rainbow;
    this.colorStripCtx.fill();
  }

  updateColorsSaturationMap() {
    const grdWhite = this.colorStripCtx.createLinearGradient(0, 0, this.saturationMap.width, 0);
    const grdBlack = this.colorStripCtx.createLinearGradient(0, 0, 0, this.saturationMap.height);
    this.saturationMapCtx.fillStyle = '#' + this.hue;
    this.saturationMapCtx.fillRect(0, 0, this.saturationMap.width, this.saturationMap.height);
    grdWhite.addColorStop(0, 'rgba(255,255,255,1)');
    grdWhite.addColorStop(1, 'rgba(255,255,255,0)');
    this.saturationMapCtx.fillStyle = grdWhite;
    this.saturationMapCtx.fillRect(0, 0, this.saturationMap.width, this.saturationMap.height);
    grdBlack.addColorStop(0, 'rgba(0,0,0,0)');
    grdBlack.addColorStop(1, 'rgba(0,0,0,1)');
    this.saturationMapCtx.fillStyle = grdBlack;
    this.saturationMapCtx.fillRect(0, 0, this.saturationMap.width, this.saturationMap.height);
  }

  toggleColorPicker() {
    this.active = !this.active;
    if (this.active) {
      this.colorInput = this.color;
    }
  }

  getCurrentColor() {
    return this.sanitizer.bypassSecurityTrustStyle('#' + this.color);
  }

  changeHue($event: any) {
    const imageData: any = this.colorStripCtx.getImageData($event.offsetX, $event.offsetY, 1, 1).data;
    this.color = this.rgbToHex(imageData[0], imageData[1], imageData[2]);
    this.hue = this.color;
    this.colorInput = this.color;
    this.updateColorsSaturationMap();
    this.onChange.emit(this.color);
  }

  changeSaturation($event: any) {
    const imageData: any = this.saturationMapCtx.getImageData($event.offsetX, $event.offsetY, 1, 1).data;
    this.color = this.rgbToHex(imageData[0], imageData[1], imageData[2]);
    this.hue = this.color;
    this.colorInput = this.color;
    this.onChange.emit(this.color);
  }

  onColorInputChange($event: any) {
    let input: string = this.colorInput;
    let rgbParts: Array<string>;
    if (input.charAt(0) === '#') {
      input = input.substr(1, input.length - 1);
    }
    if (this.validateHex(input)) {
      rgbParts = this.hexToRgb(input).split(',');
      this.color = input;
      this.hue = rgbParts[0] === rgbParts[1] && rgbParts[0] === rgbParts[2] ? 'ff0000' : this.color;
      this.onChange.emit(this.color);
      this.updateColorsSaturationMap();
    }
  }

  rgbToHex(r: number, g: number, b: number) {
    return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  hexToRgb(hex: any) {
    const bigint: number = parseInt(hex, 16);
    const r: number = (bigint >> 16) & 255;
    const g: number = (bigint >> 8) & 255;
    const b: number = bigint & 255;
    return r + ',' + g + ',' + b;
  }

  validateHex(value: any) {
    const regex: any = /^(?=(?:.{3}|.{6})$)[0-9a-fA-F]*$/;
    return regex.test(value);
  }

  onHueMouseDown($event: any) {
    this.hueIsDragged = true;
    this.changeHue($event);
  }

  onSaturationMouseDown($event: any) {
    this.saturationIsDragged = true;
    this.changeSaturation($event);
  }

  @HostListener('onMouseMove', ['$event'])
  onMouseMove($event: any) {
    if (this.hueIsDragged) {
      this.changeHue($event);
    }
    if (this.saturationIsDragged) {
      this.changeSaturation($event);
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp($event: any) {
    this.hueIsDragged = false;
    this.saturationIsDragged = false;
  }

  getWindowAnimationState() {
    return this.active ? 'show' : 'hide';
  }

  onMouseLeave($event: any) {
    this.hueIsDragged = false;
    this.saturationIsDragged = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick($event: any) {
    let clickedOutsideOfMainElement = true;
    const path: any =
      $event.path || ($event.composedPath && $event.composedPath()) || this.sharedService.composedPath($event.target);
    if (this.active) {
      for (const item in path) {
        if (_.indexOf(path[item].classList, 'colorpicker') > -1) {
          clickedOutsideOfMainElement = false;
        }
      }
      if (clickedOutsideOfMainElement) {
        this.toggleColorPicker();
      }
    }
  }
}
